import { useCallback, useMemo, useState } from 'react';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import type {
  ResolvedRhfSelectOption,
  RhfSelectActionOption,
} from './RhfSelect';

type UseRhfSelectActionOptionParams<R, V> = {
  actionOption?: RhfSelectActionOption<R, V>;
  onCommitValue: (value: V | '') => void;
  inputValue?: string;
};

export function useRhfSelectActionOption<I, R = unknown, V = string>({
  actionOption,
  onCommitValue,
  inputValue = '',
}: UseRhfSelectActionOptionParams<R, V>) {
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionInputValue, setActionInputValue] = useState('');

  const filterRegularOptions = useMemo(
    () => createFilterOptions<ResolvedRhfSelectOption<I, V>>(),
    []
  );

  const appendActionOption = useCallback(
    (base: ResolvedRhfSelectOption<I, V>[]) => {
      if (!actionOption) {
        return base;
      }
      return [
        ...base,
        {
          value: '__rhf_select_action__' as V,
          label: actionOption.label,
          item: null as unknown as I,
          isActionOption: true,
        },
      ];
    },
    [actionOption]
  );

  const trySelectActionOption = useCallback(
    (newValue: ResolvedRhfSelectOption<I, V> | string | null) => {
      if (typeof newValue !== 'string' && newValue?.isActionOption) {
        setActionInputValue(inputValue.trim());
        setActionDialogOpen(true);
        return true;
      }
      return false;
    },
    [inputValue]
  );

  const filterOptions = useCallback(
    (
      optionList: ResolvedRhfSelectOption<I, V>[],
      params: FilterOptionsState<ResolvedRhfSelectOption<I, V>>
    ) => {
      const action = optionList.find((option) => option.isActionOption);
      const regular = filterRegularOptions(
        optionList.filter((option) => !option.isActionOption),
        params
      );
      return action ? [...regular, action] : regular;
    },
    [filterRegularOptions]
  );

  const closeActionDialog = useCallback(() => {
    setActionDialogOpen(false);
    setActionInputValue('');
  }, []);

  const handleActionSuccess = useCallback(
    async (result: R) => {
      if (!actionOption) {
        return;
      }
      await actionOption.onComplete?.(result);
      const nextValue = actionOption.mapToValue?.(result);
      if (nextValue !== undefined) {
        onCommitValue(nextValue);
      }
      setActionDialogOpen(false);
      setActionInputValue('');
    },
    [actionOption, onCommitValue]
  );

  return {
    appendActionOption,
    trySelectActionOption,
    filterOptions,
    actionDialogOpen,
    actionInputValue,
    closeActionDialog,
    handleActionSuccess,
  };
}
